import {
  type AppPermission,
  type ModuleAccessLevel,
  type ModuleId,
  RESOURCE_MODULE_CATALOG,
  type User,
} from "@yalana/contracts";

// Permissions that gate write-only operations — "read/all" does NOT imply these.
// Must stay in sync with requireModuleAccess("requests", "write") on the API side.
const WRITE_ONLY_PERMISSIONS = new Set<AppPermission>(["requests.write"]);

function checkRules(
  user: User,
  permission: AppPermission,
  allowReadAction: boolean,
): boolean {
  const rules = user.permissionRules;
  if (rules === undefined) return false;
  return rules.some(
    (r) =>
      !r.inverted &&
      (r.subject === permission || r.subject === "all") &&
      (r.action === "use" ||
        r.action === "manage" ||
        (allowReadAction && r.action === "read")),
  );
}

export function hasPermission(
  user: User | null | undefined,
  permission: AppPermission,
): boolean {
  if (!user) return false;
  if (user.platformAdmin === true) return true;

  if (user.permissionRules !== undefined) {
    const isWriteOnly = WRITE_ONLY_PERMISSIONS.has(permission);
    return checkRules(user, permission, !isWriteOnly);
  }

  return (user.permissions ?? []).includes(permission);
}

export function hasModuleAccess(
  user: User | null | undefined,
  moduleId: ModuleId,
  level: ModuleAccessLevel,
): boolean {
  if (!user) return false;
  if (user.platformAdmin === true) return true;
  const perms = RESOURCE_MODULE_CATALOG[moduleId].levels[level];
  const check = level === "write" ? canWrite : hasPermission;
  return perms.every((p) => check(user, p));
}

export function canWrite(
  user: User | null | undefined,
  permission: AppPermission,
): boolean {
  if (!user) return false;
  if (user.platformAdmin === true) return true;

  if (user.permissionRules !== undefined) {
    return checkRules(user, permission, false);
  }

  return (user.permissions ?? []).includes(permission);
}
